import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Button } from '@chillout/ui'
import { AdminModal } from '../shared/Modal/AdminModal'
import { ModalActions } from '../shared/Modal/ModalActions'
import { sortChangesEntries, type AuditLogEntry } from './auditLogConstants'

interface AuditLogDetailModalProps {
  entry: AuditLogEntry | null
  onClose: () => void
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View className="mt-5">
      <Text className="text-xs font-bold uppercase tracking-[0.8px] text-secondary">{label}</Text>
      <View className="mt-1">{children}</View>
    </View>
  )
}

function toTitleCase(str: string): string {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatDetailValue(val: unknown): { display: string; id?: string } {
  if (val && typeof val === 'object' && 'name' in val) {
    const obj = val as { name: string; id?: string }
    return { display: obj.name, id: obj.id }
  }
  if (typeof val === 'string') return { display: toTitleCase(val) }
  return { display: String(val ?? 'N/A') }
}

export function AuditLogDetailModal({ entry, onClose }: AuditLogDetailModalProps) {
  const { t, i18n } = useTranslation()

  if (!entry) return null

  const date = new Date(entry.createdAt)
  const timestamp = date.toLocaleString(i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

  return (
    <AdminModal
      visible
      onClose={onClose}
      closeAccessibilityLabel={t('auditLog.detail.closeAccessibilityLabel')}
      title={t(`auditLog.eventTypes.${entry.eventType}` as never)}
      actions={
        <ModalActions>
          <Button
            label={t('auditLog.detail.done')}
            variant="secondary"
            onPress={onClose}
          />
        </ModalActions>
      }
    >
      {(entry.targetEntityName || entry.targetEntityType) ? (
        <DetailRow label={t('auditLog.detail.targetLabel')}>
          <Text className="text-sm font-normal text-primary">
            {entry.targetEntityName ?? toTitleCase(entry.targetEntityType ?? '')}
          </Text>
          {entry.targetEntityId ? (
            <Text className="text-xs font-normal text-muted">
              {entry.targetEntityId}
            </Text>
          ) : null}
        </DetailRow>
      ) : null}

      <DetailRow label={t('auditLog.detail.changesLabel')}>
        {entry.changes ? (
          <View className="gap-2">
            {sortChangesEntries(Object.entries(entry.changes)).map(([key, val]) => {
              const label = toTitleCase(key)
              if (val && typeof val === 'object' && 'before' in val && 'after' in val) {
                const before = formatDetailValue((val as { before: unknown }).before)
                const after = formatDetailValue((val as { after: unknown }).after)
                return (
                  <Text key={key} className="text-sm font-normal text-primary">
                    <Text className="font-semibold">{label}: </Text>
                    {before.display}{before.id ? <Text className="text-xs text-muted"> (...{before.id.slice(-5)})</Text> : null}
                    {' → '}
                    {after.display}{after.id ? <Text className="text-xs text-muted"> (...{after.id.slice(-5)})</Text> : null}
                  </Text>
                )
              }
              if (val === true) {
                return <Text key={key} className="text-sm font-semibold text-primary">{label}</Text>
              }
              return (
                <Text key={key} className="text-sm font-normal text-primary">
                  <Text className="font-semibold">{label}: </Text>
                  {String(val)}
                </Text>
              )
            })}
          </View>
        ) : (
          <Text className="text-sm font-normal text-secondary">
            {t('auditLog.detail.noChanges')}
          </Text>
        )}
      </DetailRow>

      {entry.reason ? (
        <DetailRow label={t('auditLog.detail.reasonLabel')}>
          <Text className="text-sm font-normal text-primary">
            {t(`common.adjustmentReasons.${entry.reason}`, { defaultValue: entry.reason })}
          </Text>
        </DetailRow>
      ) : null}

      {entry.note ? (
        <DetailRow label={t('auditLog.detail.noteLabel')}>
          <Text className="text-sm font-normal text-primary">{entry.note}</Text>
        </DetailRow>
      ) : null}

      <DetailRow label={t('auditLog.detail.actionByLabel')}>
        <Text className="text-sm font-normal text-primary">
          {entry.actorName ?? '—'}
        </Text>
        {entry.actorEmail ? (
          <Text className="text-xs font-normal text-secondary">{entry.actorEmail}</Text>
        ) : null}
      </DetailRow>

      <DetailRow label={t('auditLog.detail.timestampLabel')}>
        <Text className="text-sm font-normal text-primary">{timestamp}</Text>
      </DetailRow>

    </AdminModal>
  )
}
